import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface FraudRow {
  readonly fraudAlertId: string;
  readonly fraudType: string;
  readonly entityType: string;
  readonly riskScore: string;
  readonly severity: string;
  readonly status: string;
  readonly complianceTriggered: boolean;
}

const FRAUD_STATUSES = ['', 'OPEN', 'INVESTIGATING', 'CONFIRMED', 'DISMISSED', 'ESCALATED'];

/**
 * M25 — Fraud Dashboard (§13).
 * Lists raised fraud alerts with their severity and whether a compliance case
 * was triggered (BR-05). Alerts open for human investigation and never
 * auto-resolve — every state change is traceable in the AI audit trail (BR-07).
 */
export function M25FraudDashboardPage(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<FraudRow[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    apiRequest<{ data: FraudRow[]; total: number }>(`/v1/ai/fraud-alerts${query}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status]);

  return (
    <main aria-label="fraud-dashboard">
      <h1>{t('m25.fraud.title')}</h1>
      <label htmlFor="fraud-status-filter">{t('m25.fraud.filterLabel')}</label>
      <select id="fraud-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
        {FRAUD_STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m25.fraud.allStatuses')}</option>)}
      </select>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m25.fraud.loading')}</p> : (
        <>
          <p aria-label="fraud-count">{t('m25.fraud.count', { count: total })}</p>
          <table aria-label="fraud-table">
            <thead>
              <tr><th>{t('m25.fraud.colType')}</th><th>{t('m25.fraud.colEntity')}</th><th>{t('m25.fraud.colRiskScore')}</th><th>{t('m25.fraud.colSeverity')}</th><th>{t('m25.fraud.colCompliance')}</th><th>{t('m25.fraud.colStatus')}</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.fraudAlertId}>
                  <td>{r.fraudType}</td>
                  <td>{r.entityType}</td>
                  <td>{decimal(r.riskScore)}</td>
                  <td>{r.severity}</td>
                  <td>{r.complianceTriggered ? t('m25.fraud.triggered') : t('m25.fraud.no')}</td>
                  <td>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
